
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFieldArray, useForm } from 'react-hook-form';
import { Plus, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { useAddTimeSlots } from '@/api/classes';
import type { GymClass } from '@/api/classes';
import { StaffSelector } from './staff-selector';
import { DaysOfWeekSelector } from './days-of-week-selector';

interface AddTimeSlotsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  gymClass: GymClass;
}

interface TimeSlotForm {
  startTime: string; // HH:mm
  endTime: string; // HH:mm
  maxParticipants: number;
  disabled: boolean;
  date: string; // YYYY-MM-DD (optional - for specific date only)
  daysOfWeek: number[]; // Days of week (all 7 = everyday)
  staffMember: string; // MongoDB ObjectId
}

interface FormData {
  slots: TimeSlotForm[];
  sameCapacityForAll: boolean;
  globalCapacity: number;
}

export function AddTimeSlotsDialog({
  open,
  onOpenChange,
  gymClass,
}: AddTimeSlotsDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const addTimeSlotsMutation = useAddTimeSlots();

  const { register, control, handleSubmit, watch, setValue, formState: { errors } } =
    useForm<FormData>({
      defaultValues: {
        slots: [
          {
            startTime: '',
            endTime: '',
            maxParticipants: 10,
            disabled: false,
            date: '',
            daysOfWeek: [0, 1, 2, 3, 4, 5, 6], // Default to all days
            staffMember: '',
          },
        ],
        sameCapacityForAll: false,
        globalCapacity: 10,
      },
    });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'slots',
  });

  const sameCapacityForAll = watch('sameCapacityForAll');

  const normalizeTime = (time: string) => {
    if (!time) return time;
    const parts = time.split(':');
    return `${parts[0]}:${parts[1]}`;
  };

  const onSubmit = async (data: FormData) => {
    try {
      const seriesId = data.sameCapacityForAll
        ? `series-${Date.now()}`
        : undefined;

      const slots = data.slots.map((slot) => {
        const slotData: any = {
          startTime: normalizeTime(slot.startTime),
          endTime: normalizeTime(slot.endTime),
          maxParticipants: data.sameCapacityForAll
            ? data.globalCapacity
            : slot.maxParticipants,
          disabled: slot.disabled,
        };

        // Handle repeatability: specific date takes priority
        if (slot.date) {
          slotData.date = slot.date;
          slotData.applyEveryDay = false;
        } else if (slot.daysOfWeek && slot.daysOfWeek.length > 0) {
          // If all 7 days selected, use applyEveryDay instead
          if (slot.daysOfWeek.length === 7) {
            slotData.applyEveryDay = true;
          } else {
            slotData.daysOfWeek = slot.daysOfWeek;
            slotData.applyEveryDay = false;
          }
        } else {
          slotData.applyEveryDay = true;
        }

        // Add staff member if selected
        if (slot.staffMember) {
          slotData.staffMember = slot.staffMember;
        }

        return slotData;
      });

      await addTimeSlotsMutation.mutateAsync({
        classId: gymClass._id,
        data: {
          seriesId,
          slots,
        },
      });

      toast({
        title: t('common.success'),
        description: t('classes.timeSlotsAdded'),
      });
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error.response?.data?.message || t('common.errorOccurred'),
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('classes.addTimeSlots')}</DialogTitle>
          <DialogDescription>
            {gymClass.name} - {gymClass.label}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="sameCapacity"
              checked={sameCapacityForAll}
              onCheckedChange={(checked) =>
                setValue('sameCapacityForAll', checked as boolean)
              }
            />
            <Label htmlFor="sameCapacity" className="cursor-pointer">
              {t('classes.sameCapacityForAll')}
            </Label>
          </div>

          {sameCapacityForAll && (
            <div className="space-y-2">
              <Label htmlFor="globalCapacity">{t('classes.capacity')}</Label>
              <Input
                id="globalCapacity"
                type="number"
                min="1"
                {...register('globalCapacity', { valueAsNumber: true })}
              />
            </div>
          )}

          <div className="space-y-4">
            {fields.map((field, index) => (
              <div
                key={field.id}
                className="p-4 border rounded-lg space-y-3 relative"
              >
                {fields.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => remove(index)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor={`slots.${index}.startTime`}>
                      {t('classes.startTime')}
                    </Label>
                    <Input
                      id={`slots.${index}.startTime`}
                      type="time"
                      {...register(`slots.${index}.startTime`, {
                        required: t('classes.startTimeRequired', 'Start time is required'),
                        pattern: {
                          value: /^\d{2}:\d{2}(:\d{2})?$/,
                          message: t('classes.invalidTimeFormat', 'Invalid time format'),
                        },
                      })}
                    />
                    {errors.slots?.[index]?.startTime && (
                      <p className="text-xs text-destructive">{errors.slots?.[index]?.startTime?.message}</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`slots.${index}.endTime`}>
                      {t('classes.endTime')}
                    </Label>
                    <Input
                      id={`slots.${index}.endTime`}
                      type="time"
                      {...register(`slots.${index}.endTime`, {
                        required: t('classes.endTimeRequired', 'End time is required'),
                        pattern: {
                          value: /^\d{2}:\d{2}(:\d{2})?$/,
                          message: t('classes.invalidTimeFormat', 'Invalid time format'),
                        },
                      })}
                    />
                    {errors.slots?.[index]?.endTime && (
                      <p className="text-xs text-destructive">{errors.slots?.[index]?.endTime?.message}</p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor={`slots.${index}.staffMember`}>
                    {t('classes.instructor', 'Instructor')} ({t('common.optional', 'Optional')})
                  </Label>
                  <StaffSelector
                    value={watch(`slots.${index}.staffMember`) || ''}
                    onChange={(value) =>
                      setValue(`slots.${index}.staffMember`, value)
                    }
                    allowClear
                    placeholder={t('classes.useClassDefault', 'Use class default')}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('classes.instructorOverrideHint', 'Leave empty to use class default instructor')}
                  </p>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>
                      {t('classes.daysOfWeek', 'Days of Week')}
                    </Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const currentDays = watch(`slots.${index}.daysOfWeek`) || [];
                        if (currentDays.length === 7) {
                          setValue(`slots.${index}.daysOfWeek`, []);
                        } else {
                          setValue(`slots.${index}.daysOfWeek`, [0, 1, 2, 3, 4, 5, 6]);
                        }
                      }}
                    >
                      {(watch(`slots.${index}.daysOfWeek`) || []).length === 7
                        ? t('classes.clearAll', 'Clear All')
                        : t('classes.selectAll', 'Select All')}
                    </Button>
                  </div>
                  <DaysOfWeekSelector
                    value={watch(`slots.${index}.daysOfWeek`) || []}
                    onChange={(days) =>
                      setValue(`slots.${index}.daysOfWeek`, days)
                    }
                  />
                  <p className="text-xs text-muted-foreground">
                    {(watch(`slots.${index}.daysOfWeek`) || []).length === 7
                      ? t('classes.allDaysSelected', 'All days selected (everyday)')
                      : t('classes.selectDaysHint', 'Select days when this slot is available')}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor={`slots.${index}.date`}>
                    {t('classes.specificDate', 'Specific Date')} ({t('common.optional', 'Optional')})
                  </Label>
                  <Input
                    id={`slots.${index}.date`}
                    type="date"
                    {...register(`slots.${index}.date`)}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('classes.specificDateNote', 'If set, this slot will only be available on this specific date')}
                  </p>
                </div>

                {!sameCapacityForAll && (
                  <div className="space-y-2">
                    <Label htmlFor={`slots.${index}.maxParticipants`}>
                      {t('classes.maxParticipants')}
                    </Label>
                    <Input
                      id={`slots.${index}.maxParticipants`}
                      type="number"
                      min="1"
                      {...register(`slots.${index}.maxParticipants`, {
                        valueAsNumber: true,
                      })}
                    />
                  </div>
                )}

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id={`slots.${index}.disabled`}
                    {...register(`slots.${index}.disabled`)}
                  />
                  <Label
                    htmlFor={`slots.${index}.disabled`}
                    className="cursor-pointer"
                  >
                    {t('classes.disabled')}
                  </Label>
                </div>
              </div>
            ))}
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={() =>
              append({
                startTime: '',
                endTime: '',
                maxParticipants: 10,
                disabled: false,
                date: '',
                daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
                staffMember: '',
              })
            }
            className="w-full"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('classes.addSlot')}
          </Button>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button type="submit" disabled={addTimeSlotsMutation.isPending}>
              {addTimeSlotsMutation.isPending
                ? t('common.saving')
                : t('common.save')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
